import { useQuery } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import type { Database } from "@/integrations/supabase/types";
import { useCurrentClinicCustomerId } from "@/hooks/useCurrentClinicCustomerId";
import { useHasPermissionSync } from "@/hooks/usePermissions";

type OwnerSearchRpcRow = {
  owner_id: string;
  owner_name: string;
  owner_document: string | null;
  owner_phones: { phone_number: string }[] | null;
  owner_emails: { email: string }[] | null;
  pet_id: string;
  pet_name: string | null;
  pet_record_number: string | null;
  pet_photo: { file_path: string } | null;
  pet_breed: { name: string } | null;
  pet_species: { name: string } | null;
  pet_status: { name: string } | null;
  pet_death_date: string | null;
};

type OwnersAddressRow = Database["public"]["Tables"]["owners_addresses"]["Row"];

/** Shape returned by our owners_addresses select (subset of Row) */
type OwnersAddressSelect = Pick<
  OwnersAddressRow,
  "id" | "owner_id" | "street" | "number" | "is_primary" | "zip_code"
>;

export type PatientSearchOption = {
  value: string;
  label: string;
  ownerId: string;
  ownerName: string;
  ownerDocument: string | null;
  ownerPrimaryPhone: string | null;
  ownerPrimaryEmail: string | null;
  ownerPrimaryAddress: string | null;
  petId: string;
  petName: string;
  petRecordNumber: string | null;
  petPhotoPath: string | null;
  petSpeciesName: string | null;
  petBreedName: string | null;
  petStatusName: string | null;
  petDeathDate: string | null;
};

const formatOwnerAddress = (address: OwnersAddressSelect | null | undefined): string | null => {
  if (!address) return null;

  const line1 = [address.street, address.number].filter((value) => Boolean(value?.trim())).join(", ");
  const line2 = [address.zip_code]
    .filter((value) => Boolean(value?.trim()))
    .join(" - ");

  const formatted = [line1, line2].filter(Boolean).join(" · ").trim();
  return formatted.length > 0 ? formatted : null;
};

const QUICK_START_PATIENT_SEARCH_KEY = "patient-search-quick-start";

export function usePatientSearchForQuickStart(searchTerm: string) {
  const { data: customerId } = useCurrentClinicCustomerId();
  const { hasPermission: canViewPatients, isLoading: isLoadingPermission } = useHasPermissionSync(
    "patients",
    "view",
  );
  const trimmed = searchTerm.trim();

  return useQuery<PatientSearchOption[]>({
    queryKey: [QUICK_START_PATIENT_SEARCH_KEY, customerId, trimmed],
    enabled: canViewPatients && !isLoadingPermission && !!customerId,
    staleTime: 1000 * 30,
    gcTime: 1000 * 60 * 5,
    refetchOnWindowFocus: false,
    queryFn: async () => {
      if (!customerId) return [];

      const hasSearch = trimmed.length > 0;
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      const { data, error } = await (supabase as any).rpc("get_owners_with_pets", {
        p_customer_id: customerId,
        p_search: hasSearch ? trimmed : null,
        p_offset: 0,
        p_limit: hasSearch ? 50 : 25,
      });

      if (error) throw error;

      const rows = (data as OwnerSearchRpcRow[] | null | undefined) ?? [];
      const ownerIds = Array.from(new Set(rows.map((r) => r.owner_id).filter(Boolean)));

      const ownerAddressByOwnerId = new Map<string, string | null>();
      if (ownerIds.length > 0) {
        const { data: addressesData, error: addressesError } = await supabase
          .from("owners_addresses")
          .select("id, owner_id, street, number, is_primary, zip_code")
          .in("owner_id", ownerIds)
          .order("is_primary", { ascending: false })
          .order("created_at", { ascending: true });

        if (addressesError) throw addressesError;

        const addresses: OwnersAddressSelect[] = addressesData ?? [];
        for (const address of addresses) {
          if (!ownerAddressByOwnerId.has(address.owner_id)) {
            ownerAddressByOwnerId.set(address.owner_id, formatOwnerAddress(address));
          }
        }
      }

      const options: PatientSearchOption[] = rows
        .filter((row) => row.owner_id && row.pet_id)
        .map((row) => {
          const ownerId = row.owner_id;
          const petId = row.pet_id;
          const petName = row.pet_name?.trim() || "Paciente sem nome";
          const ownerName = row.owner_name?.trim() || "Responsável sem nome";
          const photo = row.pet_photo;
          const photoPath =
            photo && typeof photo === "object" && "file_path" in photo ? (photo.file_path as string) : null;

          return {
            value: `${ownerId}:${petId}`,
            label: `${petName} – ${ownerName}`,
            ownerId,
            ownerName,
            ownerDocument: row.owner_document ?? null,
            ownerPrimaryPhone: row.owner_phones?.[0]?.phone_number ?? null,
            ownerPrimaryEmail: row.owner_emails?.[0]?.email ?? null,
            ownerPrimaryAddress: ownerAddressByOwnerId.get(ownerId) ?? null,
            petId,
            petName,
            petRecordNumber: row.pet_record_number ?? null,
            petPhotoPath: photoPath,
            petSpeciesName: row.pet_species?.name ?? null,
            petBreedName: row.pet_breed?.name ?? null,
            petStatusName: row.pet_status?.name ?? null,
            petDeathDate: row.pet_death_date ?? null,
          };
        });

      if (!hasSearch) {
        return options.slice(0, 15);
      }
      return options;
    },
  });
}

export { QUICK_START_PATIENT_SEARCH_KEY };
